import React from "react";
import { AutoComplete, Button, Input, Popover, Tooltip, Typography } from "antd";
import { CloseOutlined, SearchOutlined } from "@ant-design/icons";
import { getRowId, getChipLabel } from "./utils";

const { Text } = Typography;

const FiltersSlidersIcon = () => (
  <svg
    className="optimisation-review__filters-icon"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M1.5 3.25H14.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M11.25 1.75V4.75"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M1.5 8H14.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M4.75 6.5V9.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M1.5 12.75H14.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M11.25 11.25V14.25"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

function SelectedSpecifications({
  searchQuery,
  searchOptions,
  searchDropdownOpen,
  searchLoading,
  visibleSelectedRows,
  previewChipRows,
  hasMoreChips,
  viewAllOpen,
  setViewAllOpen,
  handleClearAll,
  handleRemoveChip,
  handleLiveSearchChange,
  handleSelectSearchOption,
  setOpenReviewFilter,
  setSearchQuery,
  setSearchOptions,
  setSearchDropdownOpen,
}) {
  return (
    <section className="optimisation-review__section optimisation-review__card">
      <div className="optimisation-review__section-head">
        <Text strong>Selected Specifications</Text>
      </div>
      <div className="optimisation-review__toolbar">
        <AutoComplete
          className="optimisation-review__search-autocomplete"
          value={searchQuery}
          options={searchOptions}
          open={searchDropdownOpen}
          filterOption={false}
          onDropdownVisibleChange={setSearchDropdownOpen}
          onSearch={handleLiveSearchChange}
          onSelect={handleSelectSearchOption}
          notFoundContent={
            searchLoading
              ? "Searching..."
              : searchQuery.trim().length >= 2
                ? "No matching specifications"
                : "Type at least 2 characters"
          }
          popupClassName="optimisation-review__search-dropdown"
        >
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search Card Number or Spec"
            className="optimisation-review__search"
            onClear={() => {
              setSearchQuery("");
              setSearchOptions([]);
              setSearchDropdownOpen(false);
            }}
          />
        </AutoComplete>
        <Button
          className="optimisation-review__filters-btn"
          icon={<FiltersSlidersIcon />}
          onClick={() => setOpenReviewFilter(true)}
        >
          Filters
        </Button>
      </div>

      {visibleSelectedRows.length === 0 ? (
        <div className="optimisation-review__empty">
          No specifications selected. Search by Card Number or Spec, or select
          rows from the ranking grid.
        </div>
      ) : (
        <div className="optimisation-review__spec-chips">
          {previewChipRows.map((row) => {
            const rowKey = getRowId(row);
            const label = getChipLabel(row);
            return (
              <span
                key={rowKey || label}
                className="optimisation-review__spec-chip"
              >
                <Tooltip
                  title={row?.task_description || row?.card_no || label}
                >
                  <span className="optimisation-review__spec-chip-label">
                    {label}
                  </span>
                </Tooltip>
                <button
                  type="button"
                  className="optimisation-review__spec-chip-remove"
                  aria-label={`Remove ${label}`}
                  onClick={() => handleRemoveChip(row)}
                >
                  <CloseOutlined />
                </button>
              </span>
            );
          })}
          {hasMoreChips && (
            <Popover
              trigger="click"
              open={viewAllOpen}
              onOpenChange={setViewAllOpen}
              placement="bottomLeft"
              overlayClassName="optimisation-review__view-all-popover"
              content={
                <div className="optimisation-review__view-all-content">
                  <div className="optimisation-review__view-all-header">
                    <Text strong>
                      All selected ({visibleSelectedRows.length})
                    </Text>
                  </div>
                  <div className="optimisation-review__view-all-list">
                    {visibleSelectedRows.map((row) => {
                      const rowKey = getRowId(row);
                      const label = getChipLabel(row);
                      return (
                        <div
                          key={rowKey || label}
                          className="optimisation-review__view-all-item"
                        >
                          <div className="optimisation-review__view-all-item-text">
                            <span className="optimisation-review__view-all-spec">
                              {label}
                            </span>
                            {(row?.card_no || row?.task_description) && (
                              <span className="optimisation-review__view-all-meta">
                                {[row?.card_no, row?.task_description]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            className="optimisation-review__view-all-remove"
                            aria-label={`Remove ${label}`}
                            onClick={() => handleRemoveChip(row)}
                          >
                            <CloseOutlined />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              }
            >
              <button
                type="button"
                className="optimisation-review__view-all-btn"
              >
                View all ({visibleSelectedRows.length})
              </button>
            </Popover>
          )}
          <button
            type="button"
            className="optimisation-review__clear-all-btn"
            onClick={handleClearAll}
          >
            Clear all
          </button>
        </div>
      )}
    </section>
  );
}

export default React.memo(SelectedSpecifications);
